import type { CSSProperties } from 'react';

import classNames from 'classnames';

import styles from './DefaultDivider.module.css';

import type { ColorName } from '../../../styles';
import { Color, getColor } from '../../../styles';

export type DividerOrientation = 'horizontal' | 'vertical';

export type DividerBorderStyle = 'dotted' | 'dashed' | 'solid' | 'none';

export interface DefaultDividerProps {
  variant?: 'default';
  /**
   * The orientation of the divider. Place inside a flexbox when setting a
   * vertical value.
   */
  orientation?: DividerOrientation;
  /** @deprecated Use `borderStyle`. */
  style?: DividerBorderStyle;
  /** Style of the border. Default is `solid`. */
  borderStyle?: DividerBorderStyle;
  /** @deprecated Use `spacing` */
  margin?: number | string;
  /** The spacing between the line. Default is `13` */
  spacing?: number | string;
  /** Set the color of the line. Default is `Kale3` */
  color?: Color | ColorName;
  /** Set the width of the line. */
  width?: number;
  /** Add a custom class name. */
  className?: string;
}

const defaultSpacing = 13;
const defaultWidth = 2;
const defaultOrientation: DividerOrientation = 'horizontal';
const defaultBorderStyle: DividerBorderStyle = 'solid';
const defaultColor = Color.Kale3;

export function DefaultDivider(props: DefaultDividerProps) {
  const {
    orientation = defaultOrientation,
    width = defaultWidth,
    spacing = props.spacing ?? props.margin ?? defaultSpacing,
    borderStyle = props.borderStyle || props.style || defaultBorderStyle,
    color = defaultColor,
  } = props;

  const className = classNames(
    styles.root,
    {
      [styles.horizontal]: orientation === 'horizontal',
      [styles.vertical]: orientation === 'vertical',
    },
    props.className
  );
  const borderColor = getColor(color);
  const borderTopWidth = orientation === 'horizontal' ? width : undefined;
  const borderLeftWidth = orientation === 'vertical' ? width : undefined;
  const marginTop = orientation === 'horizontal' ? spacing : 0;
  const marginBottom = orientation === 'horizontal' ? spacing : 0;
  const marginLeft = orientation === 'vertical' ? spacing : 0;
  const marginRight = orientation === 'vertical' ? spacing : 0;

  const style: CSSProperties = {
    borderColor,
    borderStyle,
    borderTopWidth,
    borderLeftWidth,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
  };

  return (
    <div
      role="separator"
      aria-orientation={props.orientation}
      className={className}
      style={style}
    />
  );
}
